import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import Button from '@/components/Button'
import { Plus } from '@/components/Icons'
import Page from '@/components/Page'
import { fetchUsersPaginated } from '@/store/actions'
import { isRequestPending } from '@/store/selectors'

import { removeFromSpace, resetPaginationState, setEditUser, setFilter, setNewUser, setSortingColumn } from './actions'
import { FETCH_SPACE_MEMBERS, REMOVE_MEMBER_FROM_SPACE } from './actionTypes'
import messages from './messages'
import {
  getFilter,
  getIsEditingUser,
  getSortingColumn,
  getStartIndex,
  getTotalUsers,
  hasBeenReset,
  mapPaginatedUsers
} from './selectors'
import SpaceMembers from './SpaceMembers'

import './index.scss'

import { useHistory, useLocation } from 'react-router-dom'

import { USER_ATTRIBUTES } from '@/components/User'

function SpaceMembersPage({
  actions,
  filterValue,
  hasBeenReset,
  isDeleting,
  isEditingUser,
  isFetchingUsers,
  sortingColumn,
  totalItemsCount,
  members
}: any) {
  const history = useHistory()
  const location = useLocation()

  const sortOrders = {
    ASC: 'ascending',
    DESC: 'descending'
  }

  const membersPerPage = 10

  const isLoading = !hasBeenReset

  const [initialLoaded, setInitialLoaded] = useState(false)

  useEffect(() => {
    if (isEditingUser) {
      actions.setEditUser(false)
    } else {
      actions.resetPaginationState()
    }

    setInitialLoaded(true)
  }, [isEditingUser, actions])

  const loadPage = (page: any, sortBy: any, sortOrder: any, filter: any) => {
    actions.setSortingColumn(sortBy, sortOrder)
    actions.setFilter(filter)

    const query: {
      attributes: string[]
      count: number
      sortOrder: any
      sortBy: any
      spaceId: string
      startIndex: number
      filter?: string
    } = {
      attributes: USER_ATTRIBUTES,
      count: membersPerPage,
      // @ts-expect-error
      sortOrder: sortOrders[sortOrder || ''],
      sortBy: sortBy,
      spaceId: ':spaceId',
      startIndex: (page - 1) * membersPerPage + 1
    }

    if (filter) {
      query.filter = USER_ATTRIBUTES.map(attribute => `${attribute} co ${JSON.stringify(filter)}`).join(' or ')
    }

    actions.fetchUsersPaginated(query, FETCH_SPACE_MEMBERS)
  }

  const navigateToEditUser = (userId: any) => {
    actions.setEditUser(true)
    history.push(`${location.pathname}/${userId}`)
  }

  const onRemoveFromSpace = async (userId: any) => {
    await actions.removeFromSpace(userId)
  }

  const onAddToSpace = () => {
    actions.setNewUser(true)
    history.push(`${location.pathname}/new`)
  }

  return (
    <Page className="space-members" isLoading={isLoading}>
      <Page.Header
        buttons={
          <Button color="primary" onClick={onAddToSpace}>
            <Plus />
            <FormattedMessage {...messages.addUser} />
          </Button>
        }
        title={<FormattedMessage {...messages.title} />}
      />

      {initialLoaded && (
        <SpaceMembers
          filterValue={filterValue}
          isDeleting={isDeleting}
          isFetchingUsers={isFetchingUsers}
          loadPage={loadPage}
          navigateToEditUser={navigateToEditUser}
          onRemoveFromSpace={onRemoveFromSpace}
          perPage={membersPerPage}
          sortingColumn={sortingColumn}
          memberCount={totalItemsCount}
          members={members}
        />
      )}
    </Page>
  )
}

SpaceMembersPage.displayName = 'SpaceMemberEditPage'

export { /** @deprecated */ SpaceMembersPage }

const mapStateToProps = (state: any) => ({
  filterValue: getFilter(state),
  hasBeenReset: hasBeenReset(state),
  isDeleting: isRequestPending(state, REMOVE_MEMBER_FROM_SPACE),
  isEditingUser: getIsEditingUser(state),
  isFetchingUsers: isRequestPending(state, FETCH_SPACE_MEMBERS),
  sortingColumn: getSortingColumn(state),
  startIndex: getStartIndex(state),
  totalItemsCount: getTotalUsers(state),
  members: mapPaginatedUsers(state)
})

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      fetchUsersPaginated,
      resetPaginationState,
      removeFromSpace,
      setEditUser,
      setFilter,
      setNewUser,
      setSortingColumn
    },
    dispatch
  )
})

SpaceMembersPage.propTypes = {
  actions: PropTypes.shape({
    fetchUsersPaginated: PropTypes.func.isRequired,
    removeFromSpace: PropTypes.func.isRequired,
    resetPaginationState: PropTypes.func.isRequired,
    setEditUser: PropTypes.func.isRequired,
    setFilter: PropTypes.func.isRequired,
    setNewUser: PropTypes.func.isRequired,
    setSortingColumn: PropTypes.func.isRequired
  }).isRequired,
  filterValue: PropTypes.string,
  hasBeenReset: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isEditingUser: PropTypes.bool,
  isFetchingUsers: PropTypes.bool,
  startIndex: PropTypes.number.isRequired,
  sortingColumn: PropTypes.object,
  totalItemsCount: PropTypes.number.isRequired,
  members: PropTypes.arrayOf(PropTypes.object)
}

export default connect(mapStateToProps, mapDispatchToProps)(SpaceMembersPage)
